import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import CustomDateInput from "../../app/common/form/CustomDateInput";
import {
  Button,
  Divider,
  Typography,
  Paper,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  IconButton,
  Grid,
  MenuItem,
  TextField,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  NewMember,
  getDefaultNewMemberValues,
} from "../../app/common/interfaces/NewMember";
import { FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import CustomTextInput from "../../app/common/form/CustomTextInput";
import CustomRadioInput from "../../app/common/form/CustomRadioInput";
import { NewFamilyMember } from "../../app/common/interfaces/NewFamilyMember";
import {
  PaymentMethod,
  usePaymentMethodOptions,
} from "../../app/common/enums/paymentType";
import CustomCheckbox from "../../app/common/form/CustomCheckBox";
import CustomSelectInput from "../../app/common/form/CustomSelectInput";
import { useStore } from "../../app/stores/store";
import { v4 as uuid } from "uuid";
import { useSubscriptionTypeOptions } from "../../app/common/enums/subscriptionType";
import { useRelationShipTypeOptions } from "../../app/common/enums/relationType";
import { useSalutationTypeOptions } from "../../app/common/enums/salutationType";
import agent from "../../app/api/agent";

interface PublicTenant {
  id: string;
  name: string;
  title: string;
  domain: string;
  minimumMembershipFee: number;
  individualMembershipFee: number;
  familyMembershipFee: number;
  allowSepaPayments: boolean;
}

export default function RegisterMemberForm() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [tenants, setTenants] = useState<PublicTenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>("");
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [member, setMember] = useState<NewMember>({
    ...getDefaultNewMemberValues(),
    paymentMethod: PaymentMethod.Payment,
  });
  const [memberRegistered, setMemberRegistered] = useState(false);
  const { memberStore } = useStore();

  useEffect(() => {
    agent.Tenants.publicList().then((list) => {
      setTenants(list);
      const tenantParam = searchParams.get("tenant");
      if (tenantParam && list.some((t) => t.id === tenantParam)) {
        setSelectedTenantId(tenantParam);
      } else if (list.length === 1) {
        setSelectedTenantId(list[0].id);
      }
      setLoadingTenants(false);
    });
  }, [searchParams]);
  const subscriptionTypeOptions = useSubscriptionTypeOptions();
  const paymentMethodOptions = usePaymentMethodOptions();
  const relationShipTypeOptions = useRelationShipTypeOptions();
  const salutationTypeOptions = useSalutationTypeOptions();
  const commonRequiredMessage = t("members.requiredField");
  const validationSchema = Yup.object({
    firstName: Yup.string().required(t("registrationForm.firstNameRequired")),
    lastName: Yup.string().required(t("registrationForm.lastNameRequired")),
    country: Yup.string().required(t("registrationForm.countryRequired")),
    city: Yup.string().required(t("registrationForm.cityRequired")),
    zipCode: Yup.string().required(t("registrationForm.zipCodeRequired")),
    memberShipFee: Yup.number().required(
      t("registrationForm.membershipFeeRequired")
    ),
    paymentMethod: Yup.string().required(
      t("registrationForm.paymentMethodRequired")
    ),
    email: Yup.string().required(t("registrationForm.emailRequired")),
    bankName: Yup.string().when("paymentMethod", {
      is: PaymentMethod.Account,
      then(schema) {
        return schema.required(t("registrationForm.bankNameRequired"));
      },
    }),
    iban: Yup.string().when("paymentMethod", {
      is: PaymentMethod.Account,
      then(schema) {
        return schema.required(t("registrationForm.ibanRequired"));
      },
    }),
    bic: Yup.string().when("paymentMethod", {
      is: PaymentMethod.Account,
      then(schema) {
        return schema.required(t("registrationForm.bicRequired"));
      },
    }),
    agreeTerms: Yup.boolean().oneOf(
      [true],
      t("registrationForm.agreeTermsRequired")
    ),
    placeOfBirth: Yup.string().required(
      `${t("registrationForm.placeOfBirth")} ${commonRequiredMessage}`
    ),
    dateOfBirth: Yup.date().required(
      `${t("registrationForm.dateOfBirth")} ${commonRequiredMessage}`
    ),
    profession: Yup.string().required(
      `${t("registrationForm.profession")} ${commonRequiredMessage}`
    ),
    familyMembers: Yup.array().of(
      Yup.object({
        firstName: Yup.string().required(t("registrationForm.firstNameRequired")),
        lastName: Yup.string().required(t("registrationForm.lastNameRequired")),
        dateOfBirth: Yup.date().required(
          `${t("registrationForm.dateOfBirth")} ${commonRequiredMessage}`
        ),
        relationShipType: Yup.number().required(
          `${t("registrationForm.relationship")} ${commonRequiredMessage}`
        ),
      })
    ),
  });

  const getCheckboxText = (values: any) => {
    if (values.paymentMethod === PaymentMethod.Account) {
      return t("registrationForm.checkboxTextAccount");
    } else {
      return t("registrationForm.checkboxTextGeneral");
    }
  };

  const handleFormSubmit = async (values: NewMember) => {
    await memberStore.registerMember({ ...values, tenantId: selectedTenantId });
    setMemberRegistered(true);
    window.scrollTo(0, 0);
  };

  const selectedTenant = tenants.find((t) => t.id === selectedTenantId);
  const minimumMembershipFee = selectedTenant?.minimumMembershipFee ?? 120;
  const individualMembershipFee = selectedTenant?.individualMembershipFee ?? 240;
  const familyMembershipFee = selectedTenant?.familyMembershipFee ?? 360;

  const minimumMembershipFeeMonthly = minimumMembershipFee / 12;
  const individualMembershipFeeMonthly = individualMembershipFee / 12;
  const familyMembershipFeeMonthly = familyMembershipFee / 12;

  const allowSepaPayments = selectedTenant?.allowSepaPayments ?? false;

  useEffect(() => {
    if (!selectedTenant) return;
    setMember((prev) => ({
      ...prev,
      memberShipFee: selectedTenant.individualMembershipFee ?? 240,
    }));
  }, [selectedTenant]);

  if (loadingTenants) {
    return (
      <Paper sx={{ p: { xs: 2, sm: 3 }, maxWidth: 600, mx: "auto", mt: 4, textAlign: "center" }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (memberRegistered) {
    const tenantDomain = selectedTenant?.domain;
    const tenantDisplayName = selectedTenant?.title || selectedTenant?.name || "";
    return (
      <Paper sx={{ p: { xs: 2, sm: 3 }, maxWidth: 600, mx: "auto", mt: 4 }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t("registrationForm.successfullyRegistered")}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t("registrationForm.successfulRegistrationMessage")}
          </Typography>
          {tenantDomain && (
            <Typography variant="body2">
              {t("registrationForm.visitWebsiteMessage")}
              <b>
                <a
                  href={`https://${tenantDomain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {tenantDisplayName}
                </a>
              </b>
            </Typography>
          )}
        </Alert>
      </Paper>
    );
  }

  if (!selectedTenantId && tenants.length > 1) {
    return (
      <Paper sx={{ p: { xs: 2, sm: 3 }, maxWidth: 600, mx: "auto", mt: 4 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          {t("registrationForm.selectOrganization")}
        </Typography>
        <TextField
          select
          fullWidth
          label={t("registrationForm.organization")}
          value={selectedTenantId}
          onChange={(e) => setSelectedTenantId(e.target.value)}
          sx={{ mb: 2 }}
        >
          {tenants.map((tenant) => (
            <MenuItem key={tenant.id} value={tenant.id}>
              {tenant.title || tenant.name}
            </MenuItem>
          ))}
        </TextField>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: { xs: 1, sm: 3 },
        maxWidth: 900,
        mx: "auto",
        mt: 4,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box
        className="MembershipApplication"
        sx={{
          mb: 3,
          px: { xs: 1, sm: 0 },
        }}
      >
        <Typography variant="h4" sx={{ mb: 2 }}>
          {t("registrationForm.membershipApplication")}
          {selectedTenant && (
            <Typography variant="subtitle1" color="text.secondary">
              {selectedTenant.title || selectedTenant.name}
            </Typography>
          )}
        </Typography>
        <Formik
          enableReinitialize
          validationSchema={validationSchema}
          initialValues={{ ...member, agreeTerms: false }}
          onSubmit={(values) => handleFormSubmit(values)}
        >
          {({ handleSubmit, isValid, isSubmitting, dirty, values }) => (
            <form onSubmit={handleSubmit} autoComplete="off">
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t("registrationForm.personalInformation")}
              </Typography>
              {/* Personal information */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomRadioInput
                    name="salutation"
                    label={t("registrationForm.salutation")}
                    flexDirection={"row"}
                    options={salutationTypeOptions}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomTextInput
                    name="firstName"
                    placeholder={t("registrationForm.firstName")}
                    label={t("registrationForm.firstName")}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomTextInput
                    name="lastName"
                    placeholder={t("registrationForm.lastName")}
                    label={t("registrationForm.lastName")}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomTextInput
                    name="profession"
                    placeholder={t("registrationForm.profession")}
                    label={t("registrationForm.profession")}
                    required
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CustomTextInput
                    name="placeOfBirth"
                    placeholder={t("registrationForm.placeOfBirth")}
                    label={t("registrationForm.placeOfBirth")}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CustomDateInput
                    name="dateOfBirth"
                    label={t("registrationForm.dateOfBirth")}
                    placeholderText={t("registrationForm.dateOfBirth")}
                    dateFormat="dd.MM.yyyy"
                    required
                  />
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" sx={{ mb: 2 }}>
                {t("registrationForm.address")}
              </Typography>
              {/* Address */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomTextInput
                    name="country"
                    placeholder={t("registrationForm.country")}
                    label={t("registrationForm.country")}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomTextInput
                    name="city"
                    placeholder={t("registrationForm.city")}
                    label={t("registrationForm.city")}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomTextInput
                    name="street"
                    placeholder={t("registrationForm.street")}
                    label={t("registrationForm.street")}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomTextInput
                    name="zipCode"
                    placeholder={t("registrationForm.zipCode")}
                    label={t("registrationForm.zipCode")}
                    required
                  />
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" sx={{ mb: 2 }}>
                {t("registrationForm.contactInformation")}
              </Typography>
              {/* Contact */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <CustomTextInput
                    name="phone"
                    placeholder={t("registrationForm.phone")}
                    label={t("registrationForm.phone")}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <CustomTextInput
                    name="mobilePhone"
                    placeholder={t("registrationForm.mobilePhone")}
                    label={t("registrationForm.mobilePhone")}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <CustomTextInput
                    name="email"
                    placeholder={t("registrationForm.email")}
                    label={t("registrationForm.email")}
                    required
                  />
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" sx={{ mb: 2 }}>
                {t("registrationForm.membership")}
              </Typography>
              {/* Membership */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CustomRadioInput
                    name="memberShipFee"
                    label=""
                    options={[
                      {
                        key: String(minimumMembershipFee),
                        text: t("registrationForm.minimumContribution", { amount: minimumMembershipFeeMonthly }),
                        value: minimumMembershipFee,
                      },
                      {
                        key: String(individualMembershipFee),
                        text: t("registrationForm.individualMembership", { amount: individualMembershipFeeMonthly }),
                        value: individualMembershipFee,
                      },
                      {
                        key: String(familyMembershipFee),
                        text: t("registrationForm.familyMembership", { amount: familyMembershipFeeMonthly }),
                        value: familyMembershipFee,
                      },
                    ]}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CustomRadioInput
                    options={subscriptionTypeOptions}
                    name="subscriptionType"
                    label={t("registrationForm.subscriptionType")}
                    flexDirection={"row"}
                  />
                </Grid>
              </Grid>

              <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
                {t("registrationForm.membershipFeeDue")}
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" sx={{ mb: 2 }}>
                {t("registrationForm.bankInformation")}
              </Typography>
              {/* Payment method */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <CustomRadioInput
                    name="paymentMethod"
                    label={t("registrationForm.paymentMethod")}
                    flexDirection={"row"}
                    options={paymentMethodOptions
                      .filter((x) => x.key !== "Block" && x.key !== "Sepa")
                      .map((option) => ({
                        ...option,
                        disabled:
                          String(option.value) ===
                          String(PaymentMethod.Account) && !allowSepaPayments,
                      }))}
                  />
                  {!allowSepaPayments && (
                    <Typography variant="caption" color="error" sx={{ display: "block", mt: -1 }}>
                      {t("registrationForm.kontoUnavailable")}
                    </Typography>
                  )}
                </Grid>

                {values.paymentMethod === PaymentMethod.Account && (
                  <>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <CustomTextInput
                        name="bankName"
                        placeholder={t("registrationForm.bankName")}
                        label={t("registrationForm.bankName")}
                        required
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <CustomTextInput
                        name="iban"
                        placeholder={t("registrationForm.iban")}
                        label={t("registrationForm.iban")}
                        required
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <CustomTextInput
                        name="bic"
                        placeholder={t("registrationForm.bic")}
                        label={t("registrationForm.bic")}
                        required
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
                {t("registrationForm.cashPaymentNote")}
              </Typography>
              <Divider sx={{ my: 2 }} />

              <CustomCheckbox
                label={getCheckboxText(values)}
                name="agreeTerms"
              />

              <Typography variant="h6" sx={{ mb: 2 }}>
                {t("registrationForm.familyMembers")}
              </Typography>

              <FieldArray
                name="familyMembers"
                render={(arrayHelpers) => (
                  <TableContainer
                    component={Paper}
                    sx={{ mt: 2, overflowX: "auto" }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("registrationForm.firstName")}</TableCell>
                          <TableCell>{t("registrationForm.lastName")}</TableCell>
                          <TableCell>{t("registrationForm.dateOfBirth")}</TableCell>
                          <TableCell>{t("registrationForm.relationship")}</TableCell>
                          <TableCell>{t("registrationForm.mekteb")}</TableCell>
                          <TableCell>{t("registrationForm.actions")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {values?.familyMembers?.map((familyMember, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <CustomTextInput
                                name={`familyMembers.${index}.firstName`}
                                placeholder={t("registrationForm.firstName")}
                                value={familyMember.firstName}
                              />
                            </TableCell>
                            <TableCell>
                              <CustomTextInput
                                name={`familyMembers.${index}.lastName`}
                                placeholder={t("registrationForm.lastName")}
                                value={familyMember.lastName}
                              />
                            </TableCell>
                            <TableCell>
                              <CustomDateInput
                                name={`familyMembers.${index}.dateOfBirth`}
                                placeholderText={t("registrationForm.dateOfBirth")}
                                dateFormat="dd.MM.yyyy"
                                value={familyMember.dateOfBirth?.toString()}
                              />
                            </TableCell>
                            <TableCell>
                              <CustomSelectInput
                                options={relationShipTypeOptions}
                                name={`familyMembers.${index}.relationShipType`}
                                placeholder={t("registrationForm.relationship")}
                                label=""
                              />
                            </TableCell>
                            <TableCell>
                              <CustomCheckbox
                                label=""
                                name={`familyMembers.${index}.mekteb`}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                color="error"
                                onClick={() => arrayHelpers.remove(index)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Button
                            type="button"
                            startIcon={<AddIcon />}
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              const newFamilyMember: NewFamilyMember = {
                                id: uuid(),
                                firstName: "",
                                lastName: values.lastName || "",
                                dateOfBirth: undefined,
                                relationShipType: undefined,
                              };
                              arrayHelpers.push(newFamilyMember);
                            }}
                          >
                            {t("registrationForm.addFamilyMember")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    </Table>
                  </TableContainer>
                )}
              />

              <Button
                variant="contained"
                color="primary"
                disabled={isSubmitting || !dirty || !isValid}
                type="submit"
                sx={{ float: "right", mt: 2, ml: 2 }}
              >
                {t("registrationForm.confirm")}
              </Button>
            </form>
          )}
        </Formik>
      </Box>
    </Paper>
  );
}
